/**
 * Personal Recipe Index Service
 *
 * Manages the lightweight recipe index for fast ingredient-based searching.
 * Loads only the minimal data needed for search (~50KB) on app startup.
 */

import { InventoryItem } from '../redux/inventorySlice';
import { Platform } from 'react-native';
import personalRecipeAssets from './personalRecipeAssets';
import { DISH_TYPE_ALIASES } from '../constants/dishTypes';
import type { RecipeIndexEntry } from './unifiedRecipeService';
import { isReadableIn } from '../utils/recipeLanguage';
import { trackEvent } from './analyticsService';

type ReaderLanguage = 'en' | 'fr';

export interface PersonalRecipeIndexEntry {
  id: string;
  title: string;
  image?: string;
  servings: string;
  readyInMinutes: number;
  /** « Préparation » and the shortest « Prêt en »; undefined when the recipe has no timeline. */
  handsOnMinutes?: number;
  readyInMinutesMin?: number;
  healthScore: number;
  dishTypes: string[];
  cuisines: string[];
  diets: string[];
  ingredients: string[]; // Normalized ingredient names
  searchText: string; // Pre-built search text
  isPersonalRecipe: true;
  nutritionScore: number;
  originalLanguage?: 'en' | 'fr' | 'unknown'; // Detected original language
  translatedTitles?: { en?: string; fr?: string };
  /** Languages the recipe is fully readable in; the discovery gate reads this (#737). */
  languages?: ('en' | 'fr')[];
  sourceUrl?: string;
  sourceRating?: number;
  sourceRatingCount?: number;
  source?: string;
  _importedAt?: string;
  newsletterName?: string;
  // Background enrichment bookkeeping (#668). Without these on the entry the
  // attempt cap in `recipeNeedsEnrichment` never applied across sessions.
  enrichmentAttemptCount?: number;
  lastScrapedAt?: string;
}

/**
 * Map the lightweight Firestore index entry to the shape the recipes screen
 * expects. Lives here rather than in the screen so the field list is tested:
 * a field dropped here is silently absent everywhere downstream.
 */
export const mapEntriesToPersonal = (entries: RecipeIndexEntry[]): PersonalRecipeIndexEntry[] =>
  entries.map(r => ({
    id: r.id,
    title: r.title,
    servings: r.servingsDisplay || r.servings?.toString() || '',
    readyInMinutes: r.readyInMinutes || 0,
    handsOnMinutes: r.handsOnMinutes,
    readyInMinutesMin: r.readyInMinutesMin,
    healthScore: 0,
    nutritionScore: 0,
    cuisines: r.cuisines || [],
    dishTypes: r.dishTypes || [],
    diets: r.diets || [],
    ingredients: r.ingredients || [],
    searchText: r.searchText || r.title.toLowerCase(),
    isPersonalRecipe: true as const,
    image: r.image,
    sourceUrl: r.sourceUrl,
    sourceRating: r.sourceRating,
    sourceRatingCount: r.sourceRatingCount,
    _importedAt: r.savedAt,
    source: r.source,
    newsletterName: r.newsletterName,
    translatedTitles: r.translatedTitles,
    languages: r.languages,
    enrichmentAttemptCount: r.enrichmentAttemptCount,
    lastScrapedAt: r.lastScrapedAt,
  })) satisfies PersonalRecipeIndexEntry[];

export interface PersonalRecipeIndex {
  version: number;
  generatedAt: string;
  totalRecipes: number;
  recipes: PersonalRecipeIndexEntry[];
}

export interface PersonalRecipeMatch {
  id: string;
  title: string;
  matchScore: number;
  availableCount: number;
  totalCount: number;
  expiringSoonCount: number;
  missingIngredients: string[];
  availableIngredients: string[];
  expiringSoonIngredients: string[];
}

class PersonalRecipeIndexService {
  private index: PersonalRecipeIndex | null = null;
  private ingredientToRecipes: Map<string, string[]> = new Map();
  private isLoaded = false;
  private loadPromise: Promise<void> | null = null;

  /**
   * Load the recipe index (should be called once on app startup)
   */
  async loadIndex(): Promise<void> {
    if (this.isLoaded) return;
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = this._loadIndexInternal();
    return this.loadPromise;
  }

  private async _loadIndexInternal(): Promise<void> {
    try {
      console.log('PersonalRecipeIndex - Loading recipe index...');
      const startTime = Date.now();

      // Use the cross-platform asset loader
      this.index = await personalRecipeAssets.loadRecipeIndex();

      // Fix image paths for web platform
      if (Platform.OS === 'web' && this.index) {
        this.index.recipes = this.index.recipes.map(recipe => ({
          ...recipe,
          image: recipe.image ? `/assets/recipes/${recipe.image}` : recipe.image
        }));
      }

      // Build ingredient-to-recipes mapping for fast lookups
      this.buildIngredientIndex();

      const loadTime = Date.now() - startTime;
      console.log(`PersonalRecipeIndex - Loaded ${this.index?.totalRecipes || 0} personal recipes in ${loadTime}ms`);

      this.isLoaded = true;
    } catch (error) {
      console.error('PersonalRecipeIndex - Failed to load index:', error);
      this.index = {
        version: 1,
        generatedAt: new Date().toISOString(),
        totalRecipes: 0,
        recipes: []
      };
      this.isLoaded = true;
    }
  }

  /**
   * Build inverted index for fast ingredient-based lookups
   */
  private buildIngredientIndex(): void {
    if (!this.index) return;

    this.ingredientToRecipes.clear();

    for (const recipe of this.index.recipes) {
      for (const ingredient of recipe.ingredients) {
        const normalized = this.normalizeIngredient(ingredient);

        if (!this.ingredientToRecipes.has(normalized)) {
          this.ingredientToRecipes.set(normalized, []);
        }

        this.ingredientToRecipes.get(normalized)!.push(recipe.id);
      }
    }

    console.log(`PersonalRecipeIndex - Built ingredient index with ${this.ingredientToRecipes.size} unique ingredients`);
  }

  /**
   * Normalize ingredient name for consistent matching
   */
  private normalizeIngredient(name: string): string {
    return name.toLowerCase()
      .replace(/container|package|bag|bottle/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Check if two ingredient names match
   */
  private isIngredientMatch(recipeIngredient: string, inventoryItem: string): boolean {
    const recipe = recipeIngredient.toLowerCase().trim();
    const inventory = inventoryItem.toLowerCase().trim();

    // Exact match
    if (recipe === inventory) return true;

    // Partial matches
    if (recipe.includes(inventory) || inventory.includes(recipe)) return true;

    // Bilingual ingredient mappings (French ↔ English)
    const mappings: Record<string, string[]> = {
      // Dairy products
      'milk': ['lait', 'quebon 2% milk', '2% milk', 'whole milk', 'skim milk'],
      'lait': ['milk', 'quebon 2% milk', '2% milk', 'whole milk', 'skim milk'],
      'cheese': ['fromage', 'cheddar cheese', 'mozzarella', 'swiss cheese', 'cheese block'],
      'fromage': ['cheese', 'cheddar cheese', 'mozzarella', 'swiss cheese'],
      'butter': ['beurre'],
      'beurre': ['butter'],
      'cream': ['crème'],
      'crème': ['cream'],
      'yogurt': ['yogourt', 'greek yogurt', 'plain yogurt', 'vanilla yogurt'],
      'yogourt': ['yogurt', 'greek yogurt', 'plain yogurt'],

      // Proteins
      'eggs': ['œufs', 'œuf', 'large eggs', 'medium eggs', 'organic eggs'],
      'œufs': ['eggs', 'egg', 'large eggs', 'medium eggs'],
      'œuf': ['egg', 'eggs', 'large eggs', 'medium eggs'],
      'chicken': ['poulet'],
      'poulet': ['chicken'],
      'beef': ['bœuf'],
      'bœuf': ['beef'],
      'pork': ['porc'],
      'porc': ['pork'],
      'fish': ['poisson'],
      'poisson': ['fish'],

      // Vegetables
      'onion': ['oignon', 'onions'],
      'oignon': ['onion', 'onions'],
      'oignons': ['onions', 'onion'],
      'tomato': ['tomate', 'tomatoes'],
      'tomate': ['tomato', 'tomatoes'],
      'tomates': ['tomatoes', 'tomato'],
      'carrot': ['carotte', 'carrots'],
      'carotte': ['carrot', 'carrots'],
      'carottes': ['carrots', 'carrot'],
      'potato': ['pomme de terre', 'potatoes'],
      'pomme de terre': ['potato', 'potatoes'],
      'pommes de terre': ['potatoes', 'potato'],
      'spinach': ['épinard', 'épinards', 'spinach in plastic bag', 'fresh spinach', 'baby spinach'],
      'épinard': ['spinach', 'fresh spinach', 'baby spinach'],
      'épinards': ['spinach', 'fresh spinach', 'baby spinach'],
      'bell pepper': ['poivron', 'red bell pepper', 'green bell pepper', 'yellow bell pepper'],
      'poivron': ['bell pepper', 'red bell pepper', 'green bell pepper'],
      'zucchini': ['courgette'],
      'courgette': ['zucchini'],
      'courgettes': ['zucchini'],
      'eggplant': ['aubergine'],
      'aubergine': ['eggplant'],
      'garlic': ['ail'],
      'ail': ['garlic'],

      // Fruits
      'apple': ['pomme', 'red apple', 'green apple', 'gala apple', 'honey crisp apple'],
      'pomme': ['apple', 'red apple', 'green apple'],
      'pommes': ['apples', 'apple'],
      'lemon': ['citron'],
      'citron': ['lemon'],

      // Pantry items
      'flour': ['farine'],
      'farine': ['flour'],
      'sugar': ['sucre'],
      'sucre': ['sugar'],
      'salt': ['sel'],
      'sel': ['salt'],
      'pepper': ['poivre'],
      'poivre': ['pepper'],
      'oil': ['huile'],
      'huile': ['oil'],
      'vinegar': ['vinaigre'],
      'vinaigre': ['vinegar'],
      'rice': ['riz'],
      'riz': ['rice'],
      'pasta': ['pâtes', 'pâte'],
      'pâtes': ['pasta'],
      'pâte': ['pasta'],
      'bread': ['pain'],
      'pain': ['bread'],

      // Herbs & Spices
      'parsley': ['persil'],
      'persil': ['parsley'],
      'thyme': ['thym'],
      'thym': ['thyme'],
      'basil': ['basilic'],
      'basilic': ['basil'],
      'vanilla': ['vanille'],
      'vanille': ['vanilla'],
      'cinnamon': ['cannelle'],
      'cannelle': ['cinnamon'],
      'nutmeg': ['muscade'],
      'muscade': ['nutmeg'],

      // Beans & Legumes
      'beans': ['haricots', 'haricot'],
      'haricots': ['beans'],
      'haricot': ['beans', 'bean'],

      // Vegetables (continued)
      'vegetables': ['légumes', 'légume'],
      'légumes': ['vegetables'],
      'légume': ['vegetable', 'vegetables'],
    };

    for (const [key, values] of Object.entries(mappings)) {
      if ((recipe.includes(key) && values.some(v => inventory.includes(v))) ||
          (inventory.includes(key) && values.some(v => recipe.includes(v)))) {
        return true;
      }
    }

    return false;
  }

  /**
   * Search personal recipes by available ingredients
   */
  async searchByIngredients(
    inventory: InventoryItem[],
    maxResults: number = 20,
    language: ReaderLanguage,
  ): Promise<PersonalRecipeMatch[]> {
    await this.loadIndex();

    if (!this.index || this.index.recipes.length === 0) {
      return [];
    }
    const readable = this.readable(language, 'bundle-ingredients');

    console.log(`PersonalRecipeIndex - Searching ${this.index.totalRecipes} personal recipes`);
    const startTime = Date.now();

    const expiringSoonItems = inventory.filter(item => item.isExpiringSoon);
    const inventoryNames = inventory.map(item => this.normalizeIngredient(item.name));

    const matches: PersonalRecipeMatch[] = [];
    const recipeScores = new Map<string, PersonalRecipeMatch>();

    // Find recipes that use available ingredients
    for (const inventoryItem of inventory) {
      const normalizedItem = this.normalizeIngredient(inventoryItem.name);

      // Find recipes containing this ingredient
      for (const recipe of readable) {
        for (const recipeIngredient of recipe.ingredients) {
          if (this.isIngredientMatch(recipeIngredient, inventoryItem.name)) {
            if (!recipeScores.has(recipe.id)) {
              recipeScores.set(recipe.id, {
                id: recipe.id,
                title: recipe.title,
                matchScore: 0,
                availableCount: 0,
                totalCount: recipe.ingredients.length,
                expiringSoonCount: 0,
                missingIngredients: [],
                availableIngredients: [],
                expiringSoonIngredients: []
              });
            }

            const match = recipeScores.get(recipe.id)!;
            if (!match.availableIngredients.includes(recipeIngredient)) {
              match.availableIngredients.push(recipeIngredient);
              match.availableCount++;

              // Check if this ingredient is expiring soon
              if (expiringSoonItems.some(expiring =>
                this.isIngredientMatch(recipeIngredient, expiring.name)
              )) {
                match.expiringSoonIngredients.push(recipeIngredient);
                match.expiringSoonCount++;
              }
            }
            break; // Only count each recipe ingredient once per inventory item
          }
        }
      }
    }

    // Calculate missing ingredients and match scores
    for (const [recipeId, match] of recipeScores) {
      const recipe = this.index.recipes.find(r => r.id === recipeId)!;

      match.missingIngredients = recipe.ingredients.filter(ingredient =>
        !match.availableIngredients.includes(ingredient)
      );

      // Calculate match score (0-100)
      match.matchScore = Math.round((match.availableCount / match.totalCount) * 100);
    }

    // Convert to array and sort by match quality
    const sortedMatches = Array.from(recipeScores.values())
      .filter(match => match.availableCount > 0) // Only recipes with at least 1 matching ingredient
      .sort((a, b) => {
        // Prioritize recipes using expiring ingredients
        if (a.expiringSoonCount !== b.expiringSoonCount) {
          return b.expiringSoonCount - a.expiringSoonCount;
        }

        // Then by match score
        if (a.matchScore !== b.matchScore) {
          return b.matchScore - a.matchScore;
        }

        // Finally by total available ingredients
        return b.availableCount - a.availableCount;
      })
      .slice(0, maxResults);

    const searchTime = Date.now() - startTime;
    console.log(`PersonalRecipeIndex - Found ${sortedMatches.length} personal recipe matches in ${searchTime}ms`);

    return sortedMatches;
  }

  /**
   * Get recipe index entry by ID
   */
  async getRecipeIndexEntry(id: string): Promise<PersonalRecipeIndexEntry | null> {
    await this.loadIndex();

    if (!this.index) return null;

    return this.index.recipes.find(recipe => recipe.id === id) || null;
  }

  /**
   * Every bundled id, ungated. This answers "is there a bundled file for this
   * recipe", not "may this reader see it" — a recipe already on the plan is
   * opened whatever language its card is in. Discovery goes through
   * `getAllRecipes(language)`.
   */
  async getBundledIds(): Promise<Set<string>> {
    await this.loadIndex();
    return new Set((this.index?.recipes || []).map(recipe => recipe.id));
  }

  /**
   * Get total number of personal recipes available
   */
  async getTotalRecipes(): Promise<number> {
    await this.loadIndex();
    return this.index?.totalRecipes || 0;
  }

  /**
   * Check if personal recipes are available
   */
  async isAvailable(): Promise<boolean> {
    await this.loadIndex();
    return this.isLoaded && (this.index?.totalRecipes || 0) > 0;
  }

  /**
   * Get all recipes from the index
   */
  async getAllRecipes(language: ReaderLanguage): Promise<PersonalRecipeIndexEntry[]> {
    await this.loadIndex();
    return this.readable(language, 'bundle');
  }

  /**
   * The index as a reader of `language` may see it. Filtered per call, not at
   * load: the same loaded index serves a language switch without a reload.
   * Reports what it hid so the "Translation health" dashboard can count it
   * (#737).
   */
  private readable(language: ReaderLanguage, surface: string): PersonalRecipeIndexEntry[] {
    const all = this.index?.recipes || [];
    const shown = all.filter(r => isReadableIn(r, language));
    const hidden = all.length - shown.length;
    if (hidden > 0) {
      trackEvent('recipe_hidden_untranslated', {
        surface,
        language,
        hidden_count: hidden,
        sample_ids: all.filter(r => !isReadableIn(r, language)).slice(0, 10).map(r => r.id),
      });
    }
    return shown;
  }

  /**
   * Search recipes by text query
   */
  async searchByText(
    query: string,
    maxResults: number = 10,
    language: ReaderLanguage,
  ): Promise<PersonalRecipeIndexEntry[]> {
    await this.loadIndex();

    if (!this.index || !query.trim()) return [];
    const readable = this.readable(language, 'bundle-search');

    const searchQuery = query.toLowerCase().trim();
    const escaped = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Dish-type queries (e.g. "soupe", "soup") match titles only to avoid false positives
    // from French measurement phrases like "c. à soupe" (tablespoon) in ingredient strings.
    const matchedDishType = Object.entries(DISH_TYPE_ALIASES).find(([, aliases]) =>
      aliases.some((a) => a.toLowerCase() === searchQuery)
    );
    const titleOnly = !!matchedDishType;
    const regexesToTest: RegExp[] = matchedDishType
      ? matchedDishType[1].map((term) => {
          const e = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          return new RegExp(`(?:^|\\s|[''\\-/,;:()])${e}`, 'i');
        })
      : [new RegExp(`(?:^|\\s|[''\\-/,;:()])${escaped}`, 'i')];

    const matches: Array<{ recipe: PersonalRecipeIndexEntry; score: number }> = [];

    for (const recipe of readable) {
      let score = 0;

      // Title match (highest priority)
      if (regexesToTest.some((re) => re.test(recipe.title))) {
        score += 100;
        if (regexesToTest.some((re) => re.test(recipe.title)) && recipe.title.toLowerCase().startsWith(searchQuery)) {
          score += 50;
        }
      }

      if (!titleOnly) {
        // Ingredient match
        const ingredientMatches = recipe.ingredients.filter(ingredient =>
          regexesToTest.some((re) => re.test(ingredient))
        ).length;
        score += ingredientMatches * 10;

        // Search text match (includes all searchable content)
        if (regexesToTest.some((re) => re.test(recipe.searchText))) {
          score += 5;
        }
      }

      if (score > 0) {
        matches.push({ recipe, score });
      }
    }

    return matches
      .sort((a, b) => b.score - a.score)
      .slice(0, maxResults)
      .map(match => match.recipe);
  }
}

export default new PersonalRecipeIndexService();